import { IonContent, IonRange } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { UiTypography } from '@/components/ui/Typography';
import { amplitudeModule } from '@/modules/amplitude';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { useOnboarding } from '@/pages/Onboarding/context';
import { OwnAiTitle } from '@/pages/Onboarding/Layouts/OwnAI/components/Title';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';

import { LureScreenFooter } from '../../components/LureScreenFooter';
import { LayoutDataType } from '../../types';

import { PersonalityStyles } from './Personality.styles';

const PERSONALITY_DEFAULT = {
  shy_dominant: 50,
  serious_flirty: 50,
  logical_emotional: 50,
  affectionate_independent: 50,
};

export const ScreenPersonality: React.FC = () => {
  const { wizard } = useOnboarding();
  const form = useFormContext<LayoutDataType>();
  const personality = form.watch('personality');

  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Personality',
      });
    }
  }, []);

  const handleNextStep = (): void => {
    userService.createOrUpdate({
      onboarding_data: {
        personality,
      },
    });
    wizard.nextStep();
  };

  return (
    <PersonalityStyles>
      <OnboardingNavigation />

      <OwnAiTitle className="personality-title">
        What kind of personality attracts you more?
      </OwnAiTitle>

      <IonContent>
        <div>
          <IonRange
            value={personality.shy_dominant}
            ticks={false}
            defaultValue={PERSONALITY_DEFAULT.shy_dominant}
            class="range-data"
            snaps={false}
            onIonChange={({ detail }) => {
              form.setValue('personality.shy_dominant', detail.value as number);
            }}
          />
          <div className="range-labels">
            <UiTypography variant="label-large">Shy</UiTypography>
            <UiTypography variant="label-large">Dominant</UiTypography>
          </div>
        </div>

        <div>
          <IonRange
            value={personality.serious_flirty}
            ticks={false}
            defaultValue={PERSONALITY_DEFAULT.serious_flirty}
            class="range-data"
            snaps={false}
            onIonChange={({ detail }) => {
              form.setValue(
                'personality.serious_flirty',
                detail.value as number,
              );
            }}
          />
          <div className="range-labels">
            <UiTypography variant="label-large">Serious</UiTypography>
            <UiTypography variant="label-large">Flirty</UiTypography>
          </div>
        </div>

        <div>
          <IonRange
            value={personality.logical_emotional}
            ticks={false}
            defaultValue={PERSONALITY_DEFAULT.logical_emotional}
            class="range-data"
            snaps={false}
            onIonChange={({ detail }) => {
              form.setValue(
                'personality.logical_emotional',
                detail.value as number,
              );
            }}
          />
          <div className="range-labels">
            <UiTypography variant="label-large">Logical</UiTypography>
            <UiTypography variant="label-large">Emotional</UiTypography>
          </div>
        </div>

        <div>
          <IonRange
            value={personality.affectionate_independent}
            ticks={false}
            defaultValue={PERSONALITY_DEFAULT.affectionate_independent}
            class="range-data"
            snaps={false}
            onIonChange={({ detail }) => {
              form.setValue(
                'personality.affectionate_independent',
                detail.value as number,
              );
            }}
          />
          <div className="range-labels">
            <UiTypography variant="label-large">Affectionate</UiTypography>
            <UiTypography variant="label-large">Independent</UiTypography>
          </div>
        </div>
      </IonContent>
      <LureScreenFooter onContinue={handleNextStep} />
    </PersonalityStyles>
  );
};
